/**
 * `Donut` — spec §F2 `Donut(items, size, centerLabel, centerSub)`: "2.8% gap between
 * slices, centre number mono".
 *
 * The mockup's `donutSVG()`, with its geometry kept exactly: outer radius `size/2 − 6`,
 * inner `0.62 × r`, first slice starting at twelve o'clock, and the `.028` gap constant —
 * which is **radians**, not a fraction of the circle. §F2 writes it as "2.8%" and the
 * mockup writes `.028`; on the 86px gauge that is a ~1px hairline between slices, where
 * 2.8% of a turn would be a 6px wedge of background. The mockup wins on look (plan Global
 * Constraints), so the literal is the contract and `GAP_RAD` names it (NOTES #229).
 *
 * Three things the mockup left out and real data forces:
 *
 * 1. **Non-positive slices are dropped.** A zero-valued item still drew a path in the
 *    mockup — a 1px stub of colour that reads as a category with data. The §F5.9 target
 *    gauge produces exactly that at 100 % of target.
 * 2. **An `r=0` guard.** `donut(items, 8)` would otherwise emit a negative radius.
 * 3. **Hover through the shared `ChartTip` portal** (§F2), like `Spark`, rather than the
 *    mockup's `data-tip` + `attachTips` pass.
 *
 * Every number on screen — the centre label, the tooltip — is an ink token, never a slice
 * colour (§F2/§G8: "text in ink tokens (never series colour for values)").
 */
import { useMemo } from 'react';

import { fmtNum } from '../../lib/format';
import { hideChartTip, showChartTip } from './chartTipStore';

/**
 * §F2's "2.8% gap", as the mockup's radian constant. Each slice is shortened by this much
 * at its trailing edge, so the gap between two neighbours is `GAP_RAD` and the next slice
 * still begins at its true cumulative angle.
 */
export const GAP_RAD = 0.028;

/** The mockup's `size / 2 - 6`: the arc's own stroke-free inset from the viewBox. */
export const DONUT_INSET = 6;

/** The mockup's `inner = r * .62` — the hole, sized so the centre number fits. */
export const DONUT_INNER_RATIO = 0.62;

export interface DonutItem {
  label: string;
  value: number;
  color: string;
}

export interface DonutProps {
  items: readonly DonutItem[];
  size?: number;
  /** Big mono number in the hole (`78%`, `1,204`). */
  centerLabel?: string;
  /** Small uppercase caption under it (`OF TARGET`). */
  centerSub?: string;
  /** §G3 decimals for the tooltip value. */
  fmt?: number;
  ariaLabel?: string;
  className?: string;
}

interface Slice extends DonutItem {
  /** Index in the *input* array — §G8's fixed categorical order survives the filter. */
  i: number;
  d: string;
  pct: number;
}

/** A point on the circle of radius `rr`, in the SVG's y-down coordinate system. */
function polar(cx: number, cy: number, angle: number, rr: number): string {
  return `${(cx + rr * Math.cos(angle)).toFixed(2)},${(cy + rr * Math.sin(angle)).toFixed(2)}`;
}

export function Donut({
  items,
  size = 130,
  centerLabel = '',
  centerSub = '',
  fmt = 1,
  ariaLabel,
  className,
}: DonutProps) {
  const slices = useMemo<Slice[]>(() => {
    const drawn = items
      .map((item, i) => ({ ...item, i }))
      .filter((item) => Number.isFinite(item.value) && item.value > 0);
    const total = drawn.reduce((s, x) => s + x.value, 0) || 1;
    const r = Math.max(1, size / 2 - DONUT_INSET);
    const inner = r * DONUT_INNER_RATIO;
    const c = size / 2;

    let a0 = -Math.PI / 2; // twelve o'clock, as the mockup
    return drawn.map((item) => {
      const sweep = (item.value / total) * Math.PI * 2;
      // A lone 100% slice would close on itself with a zero-length arc, so the gap is
      // never allowed to eat more than the slice it is trimming.
      const a1 = a0 + Math.max(sweep - GAP_RAD, 0.001);
      const large = a1 - a0 > Math.PI ? 1 : 0;
      const d =
        `M${polar(c, c, a0, r)} A${r},${r} 0 ${large} 1 ${polar(c, c, a1, r)}` +
        ` L${polar(c, c, a1, inner)} A${inner},${inner} 0 ${large} 0 ${polar(c, c, a0, inner)} Z`;
      a0 += sweep;
      return { ...item, d, pct: (item.value / total) * 100 };
    });
  }, [items, size]);

  const tip = (s: Slice) => `${fmtNum(s.value, fmt)} (${Math.round(s.pct)}%)`;

  return (
    <svg
      className={`chart donut${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel ?? `${centerLabel} ${centerSub}`.trim()}
      data-slices={slices.length}
      onMouseLeave={hideChartTip}
    >
      {slices.map((s) => (
        <path
          key={s.i}
          d={s.d}
          fill={s.color}
          data-donut-slice={s.i}
          data-label={s.label}
          data-value={tip(s)}
          style={{ cursor: 'pointer' }}
          onMouseEnter={(e) => showChartTip(e, s.label, tip(s))}
          onMouseMove={(e) => showChartTip(e, s.label, tip(s))}
          onMouseLeave={hideChartTip}
        />
      ))}
      {centerLabel ? (
        <text
          x={size / 2}
          y={size / 2 - 1}
          textAnchor="middle"
          fontSize={size / 5.4}
          fontWeight={800}
          fill="#22301F"
          fontFamily="ui-monospace,monospace"
          data-donut-center=""
        >
          {centerLabel}
        </text>
      ) : null}
      {centerSub ? (
        <text
          x={size / 2}
          y={size / 2 + size / 8.5}
          textAnchor="middle"
          fontSize={8.6}
          fill="#8B958A"
          letterSpacing=".5"
          data-donut-sub=""
        >
          {centerSub}
        </text>
      ) : null}
    </svg>
  );
}

export default Donut;
